import { useUser } from "contexts/UserContext";
import { getSessionUser } from "services/users/auth";
import i18n from "i18next";
import moment, { MomentInput } from "moment-timezone";
import { useMemo } from "react";

// The examples are the English rendering; every entry follows the UI language.
const DateFormat = {
  dateLong: { year: "numeric", month: "long", day: "numeric" }, // March 5, 2026
  dateShort: { year: "numeric", month: "short", day: "numeric" }, // Mar 5, 2026
  monthDay: { month: "short", day: "numeric" }, // Mar 5
  weekdayDate: { weekday: "short", month: "short", day: "numeric" }, // Thu, Mar 5
  weekday: { weekday: "long" }, // Thursday
  // No date, deliberately: this is the (weekday, time) pair the server takes for a series.
  weeklySeriesTime: { weekday: "long", hour: "numeric", minute: "2-digit" }, // Thursday 2:30 PM

  sessionDateTime: {
    weekday: "long",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }, // Thursday, Mar 5, 2:30 PM
  dateTime: {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }, // Mar 5, 2026, 2:30 PM

  clockTime: { timeStyle: "short" }, // 2:30 PM, and 08:05 where the locale pads the hour
  clockTimeWithSeconds: { timeStyle: "medium" }, // 2:30:09 PM
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

export type DateFormatKey = keyof typeof DateFormat;

export function useUserTimezone() {
  const timezoneId = useUser().profile.timezone.id;

  return useMemo(
    () => ({
      timezoneId,
      moment: timezoneMoment(timezoneId),
      format: (date: MomentInput, formatStr: DateFormatKey) =>
        formatDate(date, timezoneId, formatStr),
    }),
    [timezoneId]
  );
}

export const timezoneMoment = (tz: string) => (time?: MomentInput) =>
  time === undefined ? moment.tz(tz) : moment.tz(time, tz);

export function localMoment(input?: MomentInput) {
  const zone = moment.tz.guess();
  return input === undefined ? moment.tz(zone) : moment.tz(input, zone);
}

export function formatDate(
  date: MomentInput,
  timezoneId: string,
  formatStr: DateFormatKey
) {
  // Parsed in the target zone, so a zone-less input means that zone's wall clock.
  const zoned = moment.tz(date, timezoneId);
  if (!zoned.isValid()) {
    return "";
  }

  return new Intl.DateTimeFormat(i18n.language, {
    timeZone: timezoneId,
    ...DateFormat[formatStr],
  }).format(zoned.valueOf());
}

/**
 * A numeric offset rather than an Intl `timeZoneName`: that option renders a prefixed
 * "GMT+01:00" ("UTC+01:00" in French) and in Chinese moves the offset ahead of the time.
 *
 * The offset is read from the formatted instant, so a March timestamp still carries the
 * offset it happened at when it is read in December.
 */
export function formatDateTimeWithOffset(date: MomentInput, timezoneId: string) {
  const zoned = moment.tz(date, timezoneId);
  if (!zoned.isValid()) {
    return "";
  }
  return `${formatDate(zoned, timezoneId, "dateTime")} ${zoned.format("Z")}`;
}

/**
 * `formatRange` rather than two formats joined: Intl drops the repeated month and picks the
 * separator per locale ("Mar 3 – 9", "3.–9. März").
 *
 * Omit `timezoneId` only for a react-big-calendar fake date, whose device-zone wall clock is
 * already the one to show (see toFakeDate); everything else names the zone.
 */
export function formatMonthDayRange(
  from: MomentInput,
  to: MomentInput,
  timezoneId?: string
) {
  // The zone only matters for a zone-less string; an instant's value is the same in any zone.
  const instant = (value: MomentInput) =>
    (timezoneId ? moment.tz(value, timezoneId) : moment.utc(value)).valueOf();

  return new Intl.DateTimeFormat(i18n.language, {
    ...(timezoneId ? { timeZone: timezoneId } : {}),
    ...DateFormat.monthDay,
  }).formatRange(instant(from), instant(to));
}

/**
 * `formatRangeToParts` rather than two formats joined: Intl drops the repeated
 * meridiem when both ends share one ("1:30 – 2:30 PM") and picks the range
 * separator per locale.
 *
 * Parts rather than the whole string because the meridiem is lowercased here and
 * only parts expose it. English is the one UI language that renders one at all.
 */
export function formatTimeRange(
  start: MomentInput,
  end: MomentInput,
  timezoneId: string
) {
  const formatter = new Intl.DateTimeFormat(i18n.language, {
    timeZone: timezoneId,
    hour: "numeric",
    minute: "2-digit",
  });
  return formatter
    .formatRangeToParts(
      moment.tz(start, timezoneId).valueOf(),
      moment.tz(end, timezoneId).valueOf()
    )
    .map(part => (part.type === "dayPeriod" ? part.value.toLowerCase() : part.value))
    .join("");
}

// For code that cannot hold a hook — a lesson-room class component. This reads the session
// singleton, which React does not track, so anything that can uses useUserTimezone instead.
export function getUserTimezoneId() {
  const user = getSessionUser(true);
  return user.profile.timezone.id;
}

export function getLocalTimezoneDisplay() {
  return getTimezoneDisplay(moment.tz.guess());
}

export function getUTCOffset(timezoneId: string) {
  return moment.tz(timezoneId).format("Z");
}

export function getTimezoneDisplay(timezoneId: string) {
  const utcOffset = moment.tz(timezoneId).format("Z");
  return `${utcOffset} ${timezoneId}`.replace(TIMEZONE_REGION_PREFIX, " ");
}

const TIMEZONE_REGION_PREFIX = /( .+\/)/gi;

/** A zone to choose from: `id` is the value to keep, `label` is only for reading. */
export type TimezoneOption = { id: string; label: string };

// Ordered by the offset each zone is on, then by label. A label drops the region to stay
// readable, so two can share one and the id breaks the tie; it is also why a label cannot
// be turned back into an id — keep the id, and render the label beside it.
export function getTimezones(): TimezoneOption[] {
  return moment.tz
    .names()
    .filter(timezoneId => timezoneId.includes("/") && !timezoneId.startsWith("Etc/"))
    .map(id => ({ id, label: getTimezoneDisplay(id), offset: moment.tz(id).utcOffset() }))
    .sort(
      (a, b) =>
        a.offset - b.offset || a.label.localeCompare(b.label) || a.id.localeCompare(b.id)
    )
    .map(({ id, label }) => ({ id, label }));
}

export function equalsLocalTimezone(timezoneId: string) {
  const localTimezoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const areTimezonesEquivalent =
    moment.tz.zone(localTimezoneId)?.name === moment.tz.zone(timezoneId)?.name;
  return areTimezonesEquivalent;
}

export const getTimeStringFromMinutes = (minutes: number | null | undefined) => {
  if (minutes) {
    const hour = Math.floor((minutes || 0) / 60);
    const min = minutes - hour * 60;
    if (hour) {
      return `${hour} hour${hour === 1 ? "" : "s"}${min > 0 ? ` ${min} min${min === 1 ? "" : "s"}` : ""}`;
    } else if (min) {
      return `${min} min${min === 1 ? "" : "s"}`;
    } else {
      return "0";
    }
  }

  return "0";
};

// Buckets a past instant into the largest unit where the diff still fits, for
// i18n relative-time rendering. `time` is signed: positive means the future.
export const getRelativeTimeLocaleOption = (time: MomentInput) => {
  const timeObj = moment.utc(time);
  // For each unit, the threshold = how many of that unit before we promote to the next coarser one.
  const unitThresholds = {
    minute: 60,
    hour: 12,
    day: 30,
    month: 12,
  } satisfies Record<string, number>;

  const range =
    (Object.keys(unitThresholds) as Array<keyof typeof unitThresholds>).find(
      unit => moment.utc().diff(timeObj, `${unit}s`) < unitThresholds[unit]
    ) ?? "year";

  return { range, time: -moment.utc().diff(timeObj, `${range}s`) };
};
